import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import {
  setStartDate,
  setInterval,
  setTip,
  setUserInfo
} from '../store/slices/donation';
import DonationForm from './AutoAjr/DonationForm';
import ConfirmationStep from './AutoAjr/ConfirmationStep';
import { handlePaymentProcess, validatePaymentForm } from '../utils/paymentUtils';
import { CardElement, useStripe, useElements } from "@stripe/react-stripe-js";
import { generateDonationSchedule, organizeDonations } from '../utils/helpers';
import { handleConfirmPayment } from '../utils/paymentUtils';
import CustomSpinner from './CustomSpinner';
import { fridays, oneTime } from '../utils/calendarTemplates';
import './AutoAjr.scss';

const AutoAjr = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const { funds, tip, userInfo, campaigns } = useSelector((state) => state.donation);
  const ref = null;
  const [activeTab, setActiveTab] = useState('weekly');
  const [selectedAmount, setSelectedAmount] = useState({
    once: 100,
    weekly: 25
  });
  const [customAmount, setCustomAmount] = useState({
    once: '',
    weekly: ''
  });
  const [paymentMethod, setPaymentMethod] = useState('card');
  const [selectedFund, setSelectedFund] = useState(null);
  const [currentStep, setCurrentStep] = useState('donation');
  const [paymentSuccessScheduleId, setPaymentSuccessScheduleId] = useState(null);
  const [processingPayment, setProcessingPayment] = useState(false);
  const [paymentSuccess, setPaymentSuccess] = useState(false);
  const [paymentError, setPaymentError] = useState(null);
  const stripe = useStripe();
  const elements = useElements();
  const [formErrors, setFormErrors] = useState({});
  const timezone = useSelector((state) => state.donation.timezone);
  const startDate =
    useSelector((state) => state.donation.startDate) || new Date();
  const endDate = useSelector((state) => state.donation.endDate);
  const hourOfDonation =
    useSelector((state) => state.donation.hourOfDonation) || 22;
  const interval = useSelector((state) => state.donation.interval);
  const [customAmounts, setCustomAmounts] = useState(Array(10).fill(0));

  // Handle global scroll redirection to right column
  useEffect(() => {
    const handleGlobalScroll = (e) => {
      // Only apply on desktop
      if (window.innerWidth > 768) {
        const rightColumn = document.querySelector('.right-column');
        if (rightColumn && !rightColumn.contains(e.target)) {
          e.preventDefault();
          const delta = e.deltaY || e.detail || e.wheelDelta;
          rightColumn.scrollTop += delta;
        }
      }
    };

    const handleResize = () => {
      // Re-evaluate scroll behavior on window resize
      if (window.innerWidth > 768) {
        // Desktop: Prevent body scroll and add wheel listener
        document.body.style.overflow = 'hidden';
        document.addEventListener('wheel', handleGlobalScroll, { passive: false });
      } else {
        // Mobile: Restore normal scroll and remove wheel listener
        document.body.style.overflow = 'auto';
        document.removeEventListener('wheel', handleGlobalScroll);
      }
    };

    // Initial setup
    handleResize();

    // Listen for window resize
    window.addEventListener('resize', handleResize);

    return () => {
      document.removeEventListener('wheel', handleGlobalScroll);
      window.removeEventListener('resize', handleResize);
      document.body.style.overflow = 'auto'; // Always restore on cleanup
    };
  }, []);

  // Clear error after 5 seconds
  useEffect(() => {
    let errorTimeout;
    if (paymentError) {
      errorTimeout = setTimeout(() => {
        setPaymentError(null);
      }, 5000);
    }
    return () => {
      if (errorTimeout) clearTimeout(errorTimeout);
    };
  }, [paymentError]);

  const donationOptions = {
    once: [25, 50, 100, 250, 500, 1500],
    weekly: [10, 25, 50, 100, 200, 500],
  };

  const getCurrentAmount = () => {
    if (customAmount[activeTab]) {
      return parseFloat(customAmount[activeTab]) || 0.00;
    }
    return selectedAmount[activeTab];
  };

  const getAutoAjrAmount = () => {
    return tip || 0.00;
  };

  const getTotalAmount = () => {
    return getCurrentAmount() + getAutoAjrAmount();
  };

  const handleDonationSubmit = (e) => {
    e.preventDefault();

    // Input validation
    if (getCurrentAmount() <= 0) {
      setPaymentError(t('autoAjr.errors.invalidAmount'));
      return;
    }
    if (!validatePaymentForm(userInfo, t)) return;
    handlePaymentSubmit();
  };

  const handlePaymentSubmit = async () => {
    try {
      console.trace(userInfo)
      // Essential fields validatio

      setProcessingPayment(true);
      setPaymentError(null);


      const template = activeTab === 'weekly' ? fridays : oneTime();
      // Process payment
      const { paymentMethodId, customerId, error } = await handlePaymentProcess({
        formValues: userInfo,
        paymentMethodType: paymentMethod,
        stripe,
        elements,
        setLoading: setProcessingPayment,
        tipAmount: getAutoAjrAmount(),
        finalTipParam: undefined,
        t,
        CardElement,
        template: activeTab === 'weekly' ? 'Fridays' : 'OneTime',
      });

      // Handle payment process errors
      if (error) {
        setPaymentError(error.message || t('autoAjr.errors.paymentProcessFailed'));
        setProcessingPayment(false);
        return;
      }

      // Generate donation schedule
      const breakdown = generateDonationSchedule(
        template['startDate'],
        template['endDate'],
        template['hourOfDonation'],
        template['interval'],
        getTotalAmount(),
        0, // Equally for now on all Fridays
        timezone,
        "Fridays"
      );


      const donationScheduleBreakdown = organizeDonations(
        [{ campaignId: '1', amount: getTotalAmount() }],
        breakdown,
      )

      // Confirm payment
      const result = await handleConfirmPayment({
        stripe,
        tipAmount: getAutoAjrAmount(),
        customerId,
        paymentMethodId,
        paymentMethodType: paymentMethod,
        donationScheduleBreakdown: donationScheduleBreakdown.map((day) => ({
          date: day.date,
          allocations: day.allocations.map((alloc) => {
            const camp = campaigns.find((c) => c.campaignId === alloc.campaignId);
            return {
              fundId: camp?.fundId,
              campaignId: camp?.campaignId,
              amount: alloc.amount
            };
          })
        })),
        donorDetails: {
          firstName: userInfo.firstName,
          lastName: userInfo.lastName,
          email: userInfo.email,
          phone: userInfo.phone,
          addressLine1: userInfo.addressLine1,
          addressLine2: userInfo.addressLine2,
          unit: userInfo.unitNumber,
          postalCode: userInfo.postalCode,
          region: userInfo.selectedRegion,
          country: userInfo.selectedCountry
        },
        template: activeTab === 'weekly' ? 'Fridays' : 'OneTime',
        timezone,
        ref,
        setLoading: setProcessingPayment,
        t
      });

      if (result.status === "success") {
        setPaymentSuccess(true);
        setPaymentSuccessScheduleId(result.scheduleId);
        setCurrentStep('confirmation');
      } else {
        setPaymentError(result.error || t('autoAjr.errors.paymentFailed'));
      }
    } catch (err) {
      console.error('Payment error:', err);
      setPaymentError(err.message || t('autoAjr.errors.unexpectedError'));
    } finally {
      setProcessingPayment(false);
    }
  };

  const handleStartOver = () => {
    setCurrentStep('donation');
    setSelectedAmount({ once: 100, weekly: 25 });
    setCustomAmount({ once: '', weekly: '' });
    setSelectedFund(null);
    setPaymentSuccess(false);
    setPaymentError(null);
    setFormErrors({});
    setUserInfo({
      firstName: "",
      middleName: "",
      lastName: "",
      email: "",
      phone: "",
      addressLine1: "",
      addressLine2: "",
      unitNumber: "",
      postalCode: "",
      selectedRegion: "",
      selectedCountry: "GB"
    });
  };

  const handleTipSubmit = (value) => {
    dispatch(setTip(value));
  };

  // Dismiss error handler
  const dismissError = () => {
    setPaymentError(null);
  };

  const renderStep = () => {
    switch (currentStep) {
      case 'confirmation':
        return (
          <ConfirmationStep
            activeTab={activeTab}
            userInfo={userInfo}
            getCurrentAmount={getCurrentAmount}
            getAutoAjrAmount={getAutoAjrAmount}
            getTotalAmount={getTotalAmount}
            handleStartOver={handleStartOver}
            scheduleId={paymentSuccessScheduleId}
          />
        );
      default:
        return (
          <DonationForm
            activeTab={activeTab}
            setActiveTab={setActiveTab}
            funds={funds}
            selectedFund={selectedFund}
            setSelectedFund={setSelectedFund}
            donationOptions={donationOptions}
            selectedAmount={selectedAmount}
            setSelectedAmount={setSelectedAmount}
            customAmount={customAmount}
            setCustomAmount={setCustomAmount}
            getCurrentAmount={getCurrentAmount}
            getAutoAjrAmount={getAutoAjrAmount}
            getTotalAmount={getTotalAmount}
            paymentMethod={paymentMethod}
            setPaymentMethod={setPaymentMethod}
            paymentError={paymentError}
            dismissError={dismissError}
            handleDonationSubmit={handleDonationSubmit}
            formErrors={formErrors}
            onTipSubmit={handleTipSubmit}
            processingPayment={processingPayment}
          />
        );
    }
  };

  return (
    <div className="auto-ajr-container">
      <div className="main-content">
        <div className="left-column">
          <div className="header-section">
            <section className="video-container">
              <video 
                src="https://barakah.ca/wp-content/uploads/2024/08/barakah-Video.mp4" 
                alt="Justice Banner" 
                autoPlay
                muted
                loop
                playsInline
              />
              <div className="video-overlay"></div>
            </section>
          </div>
          <div className="title-section">
            <h1>
              {t('savingLives')}
            </h1>
            <p className="subtitle">
              {t('uniqueApproach')}
            </p>
            <br />
          </div>
        </div>
        <div className="right-column">
          {/* Error Notification */}
          {paymentError && (
            <div className="error-notification" role="alert">
              <strong className="error-title">Error: </strong>
              <span className="error-message">{paymentError}</span>
              <button
                onClick={dismissError}
                className="close-button"
                aria-label="Close"
              >
                <span className="close-icon">&times;</span>
              </button>
            </div>
          )}

          {/* Loading Overlay */}
          {processingPayment && (
            <div className="loading-overlay">
              <div className="loading-box">
                <CustomSpinner size="large" />
                <p className="loading-text">{t('autoAjr.processingPayment')}</p>
              </div>
            </div>
          )}

          {renderStep()}
        </div>
      </div>
    </div>
  );
};

export default AutoAjr;
